import React, { useState } from "react";
import {
  useListMasterDealers,
  useCreateMasterDealer,
  useArchiveMasterDealer,
  useRestoreMasterDealer,
  getListMasterDealersQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Loader2, Archive, RotateCcw } from "lucide-react";

export function MasterDealersList() {
  const [showArchived, setShowArchived] = useState(false);
  const { data: dealersPage, isLoading, error } = useListMasterDealers(
    showArchived ? { includeArchived: true } : undefined,
  );
  const dealers = dealersPage?.items ?? [];
  const [open, setOpen] = useState(false);

  if (isLoading) return <div className="p-6 animate-pulse"><div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div><div className="h-64 bg-gray-200 rounded"></div></div>;
  if (error) return <div className="p-6 text-red-500">Failed to load master dealers.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0B132B]">Master Dealers</h1>
        <Button onClick={() => setOpen(true)} className="bg-[#E6007E] hover:bg-[#c4006b] text-white">
          <Plus className="w-4 h-4 mr-2" /> Add master dealer
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg text-[#0B132B]">All master dealers</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              id="md-show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="md-show-archived" className="text-sm text-gray-500">Show archived</Label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Dealer Code</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dealers.map((dealer) => (
                <TableRow key={dealer.id} className={dealer.archivedAt ? "opacity-60" : undefined}>
                  <TableCell className="font-medium">{dealer.name}</TableCell>
                  <TableCell>{dealer.dealerCode}</TableCell>
                  <TableCell>{dealer.contactEmail || '-'}</TableCell>
                  <TableCell>
                    {dealer.archivedAt ? (
                      <Badge variant="secondary">Archived</Badge>
                    ) : (
                      <StatusBadge status={dealer.status} />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DealerRowActions id={dealer.id} name={dealer.name} archived={!!dealer.archivedAt} />
                  </TableCell>
                </TableRow>
              ))}
              {dealers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center p-8 text-gray-500">No master dealers found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddMasterDealerDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function DealerRowActions({
  id,
  name,
  archived,
}: {
  id: number;
  name: string;
  archived: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const archive = useArchiveMasterDealer();
  const restore = useRestoreMasterDealer();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListMasterDealersQueryKey() });

  const onArchive = async () => {
    try {
      await archive.mutateAsync({ id });
      invalidate();
      toast({ title: "Master dealer archived", description: `${name} has been archived.` });
    } catch {
      toast({ title: "Could not archive", description: "Please try again.", variant: "destructive" });
    }
  };

  const onRestore = async () => {
    try {
      await restore.mutateAsync({ id });
      invalidate();
      toast({ title: "Master dealer restored", description: `${name} is active again.` });
    } catch {
      toast({ title: "Could not restore", description: "Please try again.", variant: "destructive" });
    }
  };

  if (archived) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onRestore}
        disabled={restore.isPending}
      >
        {restore.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-2" /> Restore</>}
      </Button>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={archive.isPending}>
          {archive.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Archive className="w-4 h-4 mr-2" /> Archive</>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This hides the master dealer from active lists. Their agents and orders are kept, and you can restore them at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onArchive} className="bg-[#E6007E] hover:bg-[#c4006b] text-white">
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AddMasterDealerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [dealerCode, setDealerCode] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const createDealer = useCreateMasterDealer();

  const reset = () => {
    setName("");
    setDealerCode("");
    setContactEmail("");
    setContactPhone("");
  };

  const submit = async () => {
    if (!name.trim() || !dealerCode.trim()) return;
    try {
      await createDealer.mutateAsync({
        data: {
          name: name.trim(),
          dealerCode: dealerCode.trim(),
          ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
          ...(contactPhone.trim() ? { contactPhone: contactPhone.trim() } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListMasterDealersQueryKey() });
      toast({ title: "Master dealer added", description: `${name.trim()} has been created.` });
      reset();
      onOpenChange(false);
    } catch {
      toast({ title: "Could not add master dealer", description: "Please check the details and try again.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Add master dealer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Business name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Coastal Fibre Partners" />
          </div>
          <div className="space-y-1.5">
            <Label>Dealer code</Label>
            <Input value={dealerCode} onChange={(e) => setDealerCode(e.target.value)} placeholder="e.g. MD-0042" />
          </div>
          <div className="space-y-1.5">
            <Label>Contact email (optional)</Label>
            <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} type="email" placeholder="name@example.co.za" />
          </div>
          <div className="space-y-1.5">
            <Label>Contact number (optional)</Label>
            <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} type="tel" placeholder="082 000 0000" />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!name.trim() || !dealerCode.trim() || createDealer.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createDealer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add master dealer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
